/** zh-CN catalogue `inspector` (W3-09; English: src/features/inspector/inspector.messages.ts; terms: docs/i18n/glossary.md). */
import type { MessageCatalog } from '../../core/util/types';

const catalog: MessageCatalog = {
  ns: 'inspector',
  locale: 'zh-CN',
  messages: {
    panelTitle: '属性面板',
    emptyTitle: '未选中任何内容',
    emptyBody: '选中一个音符、关键帧、区段或判定线。未选中任何内容时，这里显示当前判定线。',
    noProject: '打开一个项目以查看其音符和判定线的属性。',
    projectSettings: '项目设置',
    rawToggle: '显示导出时写入的确切 JSON（原始数据）',
    rawUnavailable: '原始数据面板不可用。',
    unitsFriendly: '易读单位（%、px、度）',
    unitsRaw: 'RPE 原始单位',
    fullSectionTip: '此部分属于完整模式；因为你的选中内容需要它，所以在此显示。',
    sectionFailed: '属性面板的这一部分停止工作。请更改选中内容后重试。',
    panelLabel: '属性面板',
    helpExpressionsTitle: '数值输入框中的表达式',
    helpExpressionsBody:
      '数值输入框既接受数字，也接受表达式。选中多项时，会按时间顺序对每一项分别计算。\n\n- `+=10` 加 10，`*-1` 取反，`=0` 将每一项都设为 0\n- `lerp(-400, 400, u)` 均匀地分布数值（u 从 0 变到 1）\n- `i` 是项目的序号，`n` 是项目数量，`v` 是其当前值\n- `rand(-30, 30)` 取一个随机值；会显示种子，以便重现结果\n\n拖动输入框的标签即可拖拽调整：Shift 为粗调，Alt 为细调。',
    helpProjectSettingsTitle: '项目设置',
    helpProjectSettingsBody:
      '整张谱面的设置集中在一处：宽高比和背景暗度、Hold 与音符大小的渲染方式、流速模式、选曲预览和导出默认值。可以从项目菜单、属性面板标题或命令面板打开。',
    helpUnitsTitle: '易读单位与 RPE 单位',
    helpUnitsBody:
      '属性面板以你看到的方式显示数值：不透明度以 % 表示，长度以默认判定线的 % 表示，尺寸以 % 表示。在完整模式下，单位按钮可将所有输入框切换为 RPE 写入的原始数字（不透明度 0-255、缩放倍数）。',
    cmdProjectSettings: '项目设置…',
    cmdFocusField: '聚焦属性面板中的输入框',
    cmdFocus: '聚焦属性面板',
    cmdToggleUnits: '属性面板：切换易读 / RPE 原始单位',
    cmdSetAtPlayhead: '在播放头处为选中的判定线插入关键帧…',
    cmdClampPreview: '将选曲预览限制在曲目范围内',
    cmdTextMode: '更改文字区段模式…',
    argProperty: '属性',
    argSection: '部分',
    argField: '输入框',
    fieldNoteTime: '音符时间',
    fieldNoteX: '音符 X',
    fieldNoteSpeed: '音符速度',
    fieldKeyBeat: '关键帧拍数',
    fieldKeyValue: '关键帧数值',
    fieldSegmentCurve: '区段缓动',
    fieldLineName: '判定线名称',
    fieldLineParent: '父线',
    fieldLineZOrder: '判定线绘制顺序',
    fieldSpeedMode: '流速模式（项目设置）',
    fieldPreview: '选曲预览（项目设置）',
    needProject: '请先打开一个项目。',
    needTextSegment: '请先选中一个文字区段。',
    needLines: '请先选中一条或多条判定线。',
    previewAlreadyFits: '选曲预览已经在曲目范围内。',
    previewNoMusic: '曲目的时长尚未知晓。',
    previewClamped: '选曲预览已限制为 {start} 秒到 {end} 秒。',
    previewBeyondAfterMusic: '选曲预览现在在曲目（{limit} 秒）结束之后才结束：Phira 会拒绝打开谱面。',
    clampAction: '限制',
    fieldNotShown: '对当前选中内容不显示该输入框。',
    txEditNotes:
      '{field, select, x {音符 X} speed {音符速度} yOffset {音符高度偏移} size {音符宽度} alpha {音符不透明度} visibleTime {音符出现时间} judgeArea {音符判定宽度} other {编辑音符}}',
    txNoteKind: '更改音符类型',
    txNoteTime: '在时间上移动音符',
    txNoteEnd: '更改 Hold 结束',
    txNoteSide: '更改音符方向',
    txNoteFake: '更改假键',
    txHitsound: '更改打击音',
    txTint: '更改音符颜色',
    txTintHit: '更改打击特效颜色',
    txKeyBeat: '移动关键帧',
    txKeyValue: '更改关键帧数值',
    txKeyJump: '更改关键帧跳变',
    txDeleteKeys: '删除关键帧',
    txEase: '更改缓动',
    txStay: '更改钩定',
    txSlice: '更改缓动切割',
    txTextMode: '更改文字区段模式',
    txLineName: '重命名判定线',
    txLinesName: '重命名判定线',
    txLineType: '更改判定线类型',
    txParent: '更改父线',
    txRotateWithParent: '更改随父线旋转',
    txZOrder: '更改绘制顺序',
    txCover: '更改遮罩',
    txGroup: '更改判定线分组',
    txTag: '更改标签颜色',
    txLineValue: '更改{prop}',
    txTexture: '更改判定线图片',
    txAttachUi: '更改 HUD 元素',
    txText: '更改判定线文字',
    txGifAuto: '更改“自动播放”',
    txResetProp: '重置{prop}',
    txAbsentFix: '设置{prop}',
    txIncline: '更改音符倾斜',
    txControl: '更改距离控制',
    txSetAtPlayhead: '在播放头处为 {count, plural, other {# 条判定线}}插入关键帧',
    txBpm: '更改速度点',
    txDeleteBpm: '删除速度点',
    txMarker: '更改标记',
    txDeleteMarkers: '删除标记',
    txMeta: '更改选曲预览',
    txPkg: '更改项目设置',
    txSpeedMode: '切换流速模式',
    txClampPreview: '限制选曲预览',
    exprFailed: '表达式没有为第 {index} 项（共 {count} 项）得到数字。',
    exprNoNumbers: '这些关键帧中没有数字。',
    setAtPlayheadNothing: '这些判定线都不能使用该属性。',
    noHolds: '选中的音符中没有 Hold。',
    sliceOrder: '切割的起点必须在终点之前。',
    done: '完成',
    cancel: '取消',
    apply: '应用',
    none: '无',
    seconds: '秒',
    px: 'px',
    deg: '度',
    percent: '%',
    percentValue: '{value, number, percent}',
    pxValue: '{value} px',
    readoutPair: '{first} · {second}',
    times: 'x',
    propMoveX: 'X 位置',
    propMoveY: 'Y 位置',
    propRotate: '旋转',
    propAlpha: '不透明度',
    propSpeed: '流速',
    propLength: '长度',
    propThickness: '粗细',
    propImageSizeX: '图片 X 尺寸',
    propImageSizeY: '图片 Y 尺寸',
    propTextSizeX: '文字 X 尺寸',
    propTextSizeY: '文字 Y 尺寸',
    propUiSizeX: 'UI X 尺寸',
    propUiSizeY: 'UI Y 尺寸',
    propBrushWidth: '画笔宽度',
    propBrushHeight: '画笔高度',
    propColor: '颜色',
    propText: '文字',
    propIncline: '倾斜',
    propPaint: '画笔',
    propGif: 'GIF 进度',
    layerBase: 'L0',
    layerN: '层级 {n}',
    noteTitle: '{kind, select, tap {Tap 音符} drag {Drag 音符} flick {Flick 音符} hold {Hold 音符} other {音符}}',
    notesTitle: '{count, plural, other {# 个音符}}',
    countTap: '{count} 个 Tap',
    countDrag: '{count} 个 Drag',
    countFlick: '{count} 个 Flick',
    countHold: '{count} 个 Hold',
    onLines: '{count, plural, other {位于 # 条判定线上}}',
    notesDetail: '{kinds} {lines}',
    copyNotes: '复制',
    deleteNotes: '删除',
    noteType: '类型',
    kindTap: 'Tap',
    kindDrag: 'Drag',
    kindFlick: 'Flick',
    kindHold: 'Hold',
    noteTypeInfo: 'Tap：点击它。Drag：只要接触到判定线即可。Flick：划过它。Hold：一直按住直到结束。',
    noteTime: '时间',
    noteTimeHelp: '音符被击打的时间。输入 12:3/4 这样的拍数、23.5s 这样的秒数，或用 +1/4 移动。',
    noteEnd: '结束',
    noteEndHelp: 'Hold 结束的时间（至少在开始后一个吸附步长）。',
    holdLength: '长度 {beats} 拍，{sec} 秒',
    toPlayhead: '移到播放头处',
    toPlayheadMulti: '最早的音符移到播放头处；其他音符保持彼此的间距。',
    noteX: 'X',
    noteXHelp: '沿判定线的位置，以 RPE 像素为单位：-675 为左边缘，675 为右边缘。',
    noteXInfo: '音符在判定线上的位置，以 RPE 像素为单位。',
    noteSide: '方向',
    sideTop: '上方',
    sideBottom: '下方',
    noteSideHelp: '音符从判定线的哪一侧到来。',
    noteSideInfo: '上方的音符从上方落到判定线上；下方的音符从下方升到判定线上。',
    noteFake: '假键',
    noteFakeLabel: '假键（装饰音符）',
    noteFakeInfo: '假键会被绘制，但永远不参与判定，也不计入数量。',
    noteMore: '更多',
    noteMoreInUse: '其中部分字段正在使用',
    noteSpeed: '速度',
    noteSpeedHelp: '音符移动距离的倍数：2 表示以两倍速度接近。',
    noteSpeedInfo: '在判定线流速之上的单个音符速度系数。',
    noteYOffset: '高度偏移',
    noteYOffsetHelp: '与判定线之间的额外距离，以 RPE 像素为单位（Phira 会按速度缩放）。',
    noteYOffsetInfo: '让音符远离判定线，而不改变其被击打的时间。',
    noteWidth: '宽度',
    noteWidthHelp: '100% 为正常的音符宽度。',
    noteWidthInfo: '音符的宽度；开启“宽度也缩放音符高度”时，高度会随之变化。',
    noteOpacity: '不透明度',
    noteOpacityHelp: '0% 会隐藏音符；它仍会被判定。',
    noteAppears: '出现',
    appearsAlways: '始终',
    appearsBefore: '击打前',
    noteAppearsSec: '击打前的秒数',
    noteAppearsHelp: '音符在被击打前这么多秒突然出现（没有淡入）。',
    noteAppearsInfo: '音符在被击打前多久变为可见。',
    noteHitsound: '打击音',
    hitsoundDefault: '{kind, select, flick {Flick} drag {Drag} other {Tap}}（默认）',
    hitsoundTap: 'Tap 音效',
    hitsoundDrag: 'Drag 音效',
    hitsoundFlick: 'Flick 音效',
    hitsoundAdd: '添加音频文件…',
    hitsoundPlay: '播放打击音',
    hitsoundInfo: '音符被击打时播放的声音；自定义文件会随谱面一起加载。',
    noteTint: '音符颜色',
    noteTintInfo: '为音符贴图染色（RPE 1.7 及更高版本）。',
    noteTintHit: '打击特效颜色',
    noteTintHitInfo: '此音符打击粒子的颜色（RPE 1.7 及更高版本）。',
    noteJudge: '判定宽度',
    noteJudgeHelp: '100% 为正常的判定宽度（每侧约 160 RPE 像素）。',
    noteJudgeInfo: '在音符旁多远处的触摸仍能击中它（RPE 1.7 及更高版本）。',
    resetDefault: '重置为默认值',
    phiraLine: 'Phira：{simultaneous} · {visible}',
    phiraSimultaneous: '{count, plural, =0 {不与其他音符多押} other {与另外 # 个音符多押}}',
    phiraVisible: '击打前 {sec} 秒可见',
    phiraVisibleAlways: '从谱面开头起可见',
    phiraNeverVisible: '以当前速度永远不可见',
    phiraUiMover: '永远不会绘制：其判定线是 UI 绑定线',
    notesSimultaneous: '这些音符中有 {count, plural, other {# 个}}与其他音符多押',
    distribute: '分布…',
    transform: '变换…',
    keyTitle: '关键帧',
    keysTitle: '{count, plural, other {# 个关键帧}}',
    keyOn: '{prop}，层级 {layer}',
    keysOnProps: '{count, plural, other {位于 # 个属性上}}',
    deleteKey: '删除关键帧',
    keyAt: '位于',
    keyAtHelp: '关键帧所在的拍数。+1/4 可将其后移。',
    keyValue: '数值',
    keyValueHelp: '此关键帧处的数值。也可以使用表达式：+=10、*-1。',
    keyText: '文字',
    keyColour: '颜色',
    keyJumpLabel: '以一个值到达，以另一个值离开',
    keyJumpInfo: '跳变会在此关键帧处瞬间改变数值（列中显示为两个半菱形）。',
    keyArrive: '到达值',
    keyLeave: '然后',
    keyJumpFirst: '第一个关键帧没有可以到达它的运动。',
    keyArriveFixed: '从它之前的钩定区段到达，因此到达值是固定的。',
    keyLast: '最后一个关键帧：之后数值保持不变。',
    keyUnitMixed: '以 RPE 原始单位显示数值：选中的属性使用不同的单位。',
    keyIgnoredByKind: 'Phira 会忽略此判定线上的这个属性。',
    keyMixedTypes: '这些关键帧存放的数值类型不同。',
    segmentTitle: '区段',
    segmentsTitle: '{count, plural, other {# 个区段}}',
    segmentSpan: '{from} 到 {to}',
    segmentCurve: '缓动',
    segmentCurveHelp: '数值如何变化到下一个关键帧。',
    segmentCurveInfo: '从此关键帧开始的区段的曲线。',
    changeCurve: '更改…',
    curveLinear: '线性（RPE 1）',
    curveStay: '钩定（保持后跳变）',
    curveBezier: '自定义贝塞尔曲线',
    curveMixed: '多种缓动',
    curveGuided: '{strength}，{shape}（RPE {id}）',
    curveFull: '{name}（RPE {id}）',
    curveSliced: '{name}，使用 {left} 到 {right} 部分',
    strengthGentle: '轻柔',
    strengthSoft: '柔和',
    strengthMedium: '适中',
    strengthStrong: '较强',
    strengthStronger: '更强',
    strengthExtreme: '极强',
    strengthSharp: '陡峭',
    strengthOvershoot: '过冲',
    strengthSpringy: '弹簧',
    strengthBouncy: '弹跳',
    shapeSlowStart: '慢起',
    shapeSlowEnd: '慢停',
    shapeSlowBoth: '两端慢',
    segmentStay: '钩定：保持，在下一个关键帧处跳变',
    segmentStayInfo: '数值保持不变，到达下一个关键帧时跳变为其数值。',
    segmentFrom: '起始值',
    segmentTo: '结束值',
    segmentToFixed: '钩定区段会保持其数值，直到下一个关键帧。',
    segmentSlice: '缓动切割（使用曲线的一部分）',
    segmentSliceStart: '起点',
    segmentSliceEnd: '终点',
    segmentSliceInfo: '只使用曲线的一部分（RPE easingLeft / easingRight）。',
    segmentSliceOnlyTween: '只有列表中的缓动可以切割。',
    segmentSliceSkipped: '已跳过 {count, plural, other {# 个区段}}：只有列表中的缓动可以切割。',
    segmentBake: '切割…',
    segmentReverse: '反转',
    segmentExtrapolate: '递推',
    segmentLink: '绑定',
    segmentUnlink: '解除绑定',
    segmentLinked: '绑定的区段（绑定组 {group}）。对其中任一区段的每次编辑都会写入所有区段（时间保持各自独立）。',
    exactPhira: 'Phira：{state, select, exact {精确} baked {导出时烘焙} nan {会除以零} other {精确}}',
    exactRpe:
      'RPE：{state, select, exact {精确} differs {绘制此缓动的方式不同} baked {导出时烘焙} ignored {保留了 Phira 会忽略的缓动} other {精确}}',
    approxSplit: '拆分 Expo / Elastic 缓动：与原曲线的偏差在 0.1% 以内。需要精确时请烘焙。',
    crossScreen: '音符穿过屏幕需要 {sec} 秒',
    crossScreenRange: '音符穿过屏幕需要 {from} 到 {to} 秒',
    crossScreenNever: '以此速度音符永远不会到达判定线',
    nanSlice: '曲线的这一部分会让 Phira 除以零（数值变为 NaN）。',
    useWholeCurve: '使用整条曲线',
    rpeDiffers: 'RPE 绘制缓动 {id} 的方式与 Phira 不同；预览显示的是 Phira 绘制的效果。',
    bakedSpeed: '经典流速模式：导出时此带缓动的流速会写为线性分段。',
    ignoredEase: '此流速区段为 RPE 保留了 RPE 缓动 {id}；Phira 按线性播放（经典流速模式）。',
    openSpeedSettings: '流速模式…',
    segmentDuration: '{beats} 拍，{sec} 秒',
    exactness: '{phira} · {rpe}',
    exactnessDuration: '{phira} · {rpe} · {duration}',
    easeSkipped: '{count, plural, other {# 个区段}}保留了原来的缓动：那里不允许使用该缓动（流速、文字或特效规则）。',
    noSegment: '最后一个关键帧不会开始任何区段。',
    textSegTitle: '文字区段',
    textMode: '模式',
    modeSwitch: '切换',
    modeTypeOn: '逐字显示',
    modeErase: '擦除',
    modeExtend: '扩展',
    modeCounter: '计数器',
    modeSwitchDesc: '在下一个关键帧之前显示第一段文字，然后切换。',
    modeTypeOnDesc: '逐个字符地显示文字。',
    modeEraseDesc: '逐个字符地移除文字。',
    modeExtendDesc: '添加或移除不同的字符（一段文字以另一段开头）。',
    modeCounterDesc: '从一个数字计数到另一个数字。',
    modeMixed: '多种模式',
    textModeInfo: 'Phira 根据两段文字决定动画；模式会帮你改写它们。',
    editMode: '编辑…',
    textFrom: '文字',
    textThen: '然后',
    textTypes: '逐字显示',
    textErases: '擦除',
    textStart: '起始文字',
    textEnd: '结束文字',
    counterFrom: '从',
    counterTo: '到',
    counterInt: '显示整数。',
    counterDec: '显示 3 位小数（有一端不是整数）。',
    guardCrash: '超出范围的缓动在逐字显示和擦除时会使 Phira 崩溃：缓动已设为线性。',
    guardCrashNow: '此缓动会超出范围：Phira 会在此区段崩溃。请选择不超出范围的缓动。',
    guardWarn: '此缓动会超出范围：Phira 会短暂显示多余或缺失的字符。',
    counterNotNumeric: '整段文字必须是一个数字。请把标签放在另一条文字线上。',
    textSplitApprox: '拆分此区段是近似的：Phira 会对字符数取整。',
    textPreview: '预览',
    textPreviewAt: '{p}%',
    textModeTitle: '文字区段模式',
    textModeSegment: '区段 {from} 到 {to}',
    textExtendNeedsPrefix: '一段文字必须以另一段开头。',
    textEmpty: '请先输入文字。',
    lineTitle: '判定线',
    lineName: '名称',
    lineType: '类型',
    typeLine: '判定线',
    typeText: '文字',
    typeImage: '图片',
    typeGif: 'GIF',
    typePaint: '画笔',
    typeUiMover: 'UI 绑定线',
    typeFull: '{label}（完整）',
    lineTypeInfo: '判定线绘制的内容。更改它会添加或移除 Phira 据以判断的数据。',
    pickImage: '选择图片',
    pickGif: '选择 GIF',
    pickElement: '选择 HUD 元素',
    noImages: '请先向项目添加一张图片。',
    noGifs: '请先向项目添加一张 GIF 图片。',
    addImage: '添加图片…',
    assetOption: '{path}',
    chip: '{text}',
    typeLossTitle: '更改判定线类型？',
    typeLossBody:
      '将{count, plural, =1 {此判定线} other {这 # 条判定线}}更改为 {type} 会移除：{what}。此操作可以撤销。',
    typeLossConfirm: '更改类型',
    lossTexture: '图片',
    lossText: '文字关键帧',
    lossPaint: '画笔关键帧',
    lossGif: 'GIF 进度关键帧',
    lossAutoLoop: '自动播放',
    lossAttachUi: 'HUD 绑定',
    lineParent: '父线',
    parentNone: '无',
    parentBlockedSelf: '此判定线',
    parentBlockedLoop: '会形成循环',
    parentHelp: '不透明度、颜色、缩放和流速永远不会继承。',
    parentInfo: '判定线随其父线移动：其位置在父线的坐标系中计量。',
    parentPromptTitle: '这些判定线应如何跟随新的父线？',
    parentKeepNumbers: '保持其数值',
    parentKeepNumbersDesc: '它们随父线移动；其数值保持不变（RPE 的行为）。',
    parentKeepPlace: '保持其当前位置',
    parentKeepPlaceDesc: '在播放头处将它们的位置换算到父线的坐标系中。',
    rotateWithParent: '随父线旋转',
    rotateWithParentSentence: '子线的位置总会随父线的旋转而绕转。此选项只决定子线自身的角度是否也一起旋转。',
    rotateNoParent: '没有父线时不起作用。',
    diagramBefore: '父线为 0 度',
    diagramTurns: '子线也会旋转',
    diagramKeeps: '子线保持其角度',
    lineZOrder: '绘制顺序',
    lineZOrderHelp: '数字越大越靠上绘制。音符总是绘制在所有判定线之上。相同时按导出顺序。',
    lineZOrderInfo: '判定线的绘制顺序。',
    zFront: '置于顶层',
    zBack: '置于底层',
    lineCover: '遮罩（隐藏线下音符）',
    lineCoverHelp: '从判定线错误一侧过来的音符会一直隐藏，直到它们越过判定线。',
    lineCoverInfo: '隐藏位于判定线后方的音符（例如速度为负时）。',
    lineCoverRaw: '导入的值为 {raw}：除 1 以外的值都表示关闭。',
    coverDiagramOn: '判定线后方的音符被隐藏',
    coverDiagramOff: '判定线后方的音符保持可见',
    lineGroup: '分组',
    groupDefault: '默认',
    lineGroupInfo: '判定线分组有助于整理判定线；它们会导出到 RPE。',
    lineTag: '标签颜色',
    lineTagInfo: '为轨道标题和预览标签着色。仅用于编辑器。',
    lengthHelp: '100% 为 Phira 判定线的默认长度。',
    thicknessPx: '粗 {px} px',
    thicknessHelp: '默认粗细的倍数；会显示以像素为单位的确切粗细。',
    imageSizeHelp: '100% 表示每个 RPE 像素对应一个图片像素（图片为 {w} x {h} px）。',
    imageSizeHelpNoSize: '100% 表示每个 RPE 像素对应一个图片像素。',
    textSizeHelp: '100% 为 54 RPE 像素。',
    uiSizeHelp: '缩放 HUD 元素。',
    brushHelp: '以设备像素为单位的画笔半径；0 或更小会清空画布。',
    brushSizeHelp: '100% 表示 Phira 在没有缩放关键帧时绘制的画笔大小。',
    animatedHint: '有动画：编辑会在 {beat} 写入关键帧。',
    staticHint: '只有一个关键帧：该数值在整张谱面中保持不变。',
    lineImage: '图片',
    lineImageMissing: '{path}（缺失）',
    imagePhiraOnly: '此图片格式只能在 Phira 中加载：RPE 读取 PNG 和 JPG。',
    imageCentred: 'Phira 会将图片居中绘制在判定线的位置。',
    gifFirstFrame: '没有 GIF 进度且未开启“自动播放”的 GIF 只显示第一帧，与 Phira 一致。',
    hitsoundMissing: '{path}（不在项目中）',
    gifRemoveWarn: '选择非 GIF 的图片会移除 GIF 进度和“自动播放”。',
    lineImageInfo: '判定线显示的图片，代替判定线图形。PNG 和 JPG 在 RPE 中也能加载。',
    lineUiElement: 'HUD 元素',
    uiPause: '暂停按钮',
    uiCombonumber: '连击数',
    uiCombo: 'COMBO 标签',
    uiScore: '分数',
    uiBar: '进度条',
    uiName: '曲名',
    uiLevel: '难度等级',
    uiRotationNote: '该元素自身的角度会忽略父线的旋转。',
    uiMoverInfo: '此判定线移动一个 HUD 元素，而不绘制任何内容。',
    uiMoverNotes: '此判定线移动一个 HUD 元素：其上的音符将不可见。',
    lineText: '文字',
    lineTextAnimated: '{count, plural, other {# 个文字关键帧}}：编辑会修改 {beat} 处的那个。',
    lineTextInfo: '判定线显示的文字（可以有多行）。',
    gifAutoLoop: '自动播放',
    gifAutoLoopHelp: '从谱面开头起以 GIF 的自然速度循环播放。',
    gifProgressInUse: '此 GIF 跟随其 GIF 进度关键帧；“自动播放”会移除它们。',
    properties: '属性',
    propStatic: '一个关键帧：{value}',
    propAnimated: '{count, plural, other {# 个关键帧}}',
    propLayers: '{count} 个层级',
    propIgnored: 'Phira 会忽略此判定线上的它',
    propReset: '重置{prop}：移除其关键帧',
    fixAlpha: '设为 100%',
    fixSpeed: '设为 10',
    usesLabel: '使用：',
    usesLayers: '{count} 个层级',
    usesColor: '颜色',
    usesScale: '缩放',
    usesIncline: '音符倾斜',
    usesControls: '距离控制',
    usesText: '文字',
    usesPaint: '画笔',
    usesGif: 'GIF',
    usesUiMover: 'UI 绑定',
    usesShow: '显示',
    advanced: '高级',
    incline: '倾斜',
    inclineHelp: '按距离倾斜音符。Phira 会在整张谱面中将其固定为起始值。',
    inclineInfo: '根据音符与判定线的距离压缩其 X（不包括 Hold）。',
    inclineAnimated: '带动画的音符倾斜（导入的）：Phira 在整张谱面中使用 {deg} 度。',
    makeStatic: '设为静态',
    controls: '距离控制',
    controlsInfo: '随音符与判定线距离变化的倍数（RPE 音符控制）。',
    rpeOnly: '仅限 RPE 的字段',
    rpeOnlyTip: 'Phira 会忽略这些字段；它们为 RPE 保留。',
    bpmfactor: 'BPM 系数',
    anchor: '锚点',
    skewControl: '斜切控制',
    skewDefault: 'RPE 默认',
    skewCustom: '自定义',
    controlAlpha: '按距离的不透明度',
    controlSize: '按距离的宽度',
    controlPos: '按距离的 X',
    controlY: '按距离的距离',
    controlAlphaDesc: '乘以音符的不透明度。',
    controlSizeDesc: '乘以音符的宽度（Hold 也包括在内）。',
    controlPosDesc: '乘以音符的 X（不包括 Hold；判定位置不会移动）。',
    controlYDesc: '乘以音符与判定线之间的绘制距离。',
    controlNotSet: '未设置：没有效果。',
    controlAdd: '添加',
    controlClear: '移除距离控制',
    controlPresets: '预设',
    presetFadeInNear: '靠近判定线时淡入',
    presetFadeOutNear: '靠近判定线时淡出',
    presetShrinkFar: '缩小远处的音符',
    presetCentreFar: '将远处的音符拉向中心',
    presetCompressFar: '拉近远处的音符',
    controlInert: '在 Phira 中无效',
    controlInertTip: '恰好有 2 个点、且第一个点的值为 1、缓动为 1 的曲线会被 Phira 忽略。',
    addThirdPoint: '添加第三个点',
    controlNoEffect: '所有值都是 1：没有效果。',
    pointX: '距离',
    pointEasing: '缓动',
    pointValue: '数值',
    addPoint: '添加点',
    removePoint: '移除点',
    graphLabel: '{name}：随音符距离变化的倍数',
    linesTitle: '{count, plural, other {# 条判定线}}',
    typeCount: '{count} 条 {type}',
    namePattern: '名称',
    namePatternHelp: "格式：'{i}' = 0 到 {last}，'{n}' = 1 到 {count}，'{name}' = 当前名称。",
    namePatternPreview: '变为：{names}',
    zOrderExprHelp: '一个数字，或 i、i+10 这样的表达式（i = 在导出顺序中的位置）。',
    setAtPlayhead: '在播放头处设置',
    setAtPlayheadHelp: '在每条选中判定线的播放头处写入一个关键帧。',
    setOpacity: '不透明度…',
    setPosition: '位置…',
    setRotation: '旋转…',
    setSpeed: '流速…',
    setOnLines: '在 {count, plural, other {# 条判定线}}上设置',
    keepCurrent: '留空可保持每条判定线的数值（只添加关键帧）。',
    textureAll: '应用于每条选中的判定线。',
    tempoTitle: '速度点',
    temposTitle: '{count, plural, other {# 个速度点}}',
    tempoBeat: '拍',
    tempoBeatZero: '第一个速度点总是位于第 0 拍。',
    tempoBpm: 'BPM',
    tempoSec: '位于 {sec}',
    tempoPolicy: '之后的项目',
    policyKeepBeats: '保持拍数',
    policyKeepTime: '保持时间',
    policyHelp:
      '保持拍数：音符和关键帧随速度在时间上移动（RPE）。保持时间：它们停留在听到的位置。此项目会记住你的选择；“曲目与时间”使用相同的选择。',
    deleteTempo: '删除速度点',
    deleteTempos: '删除速度点',
    tempoRetimed: '已重新计时 {count, plural, other {# 个项目}}（{snapped} 个吸附到 1/1920 拍）。',
    openTiming: '打开“曲目与时间”',
    markerTitle: '标记',
    markersTitle: '{count, plural, other {# 个标记}}',
    markerName: '名称',
    markerBeat: '拍',
    markerColour: '颜色',
    deleteMarker: '删除标记',
    deleteMarkers: '删除标记',
    psTitle: '项目设置',
    psDescription: '整张谱面的设置。每项更改都可以撤销。',
    psDisplay: '显示',
    psGameplay: '游戏渲染',
    psSpeed: '流速模式',
    psPreview: '选曲预览',
    psExport: '导出默认值',
    aspectRatio: '宽高比',
    aspectHelp: '游玩区域的宽度除以高度（16:9 = 1.778）。',
    aspectPreset: '预设',
    aspectCustom: '自定义',
    forceAspect: '强制使用此宽高比',
    forceAspectHelp: 'Phira 会在所有屏幕上为游玩区域加黑边，使其保持此比例。',
    backgroundDim: '背景暗度',
    backgroundDimHelp: '谱面背后的曲绘绘制得暗多少。',
    holdPartialCover: 'Hold 在尾部越过前保持可见',
    holdPartialCoverHelp: '开启遮罩时，Hold 只有在尾部到达判定线后方后才会被隐藏。',
    noteUniformScale: '宽度也缩放音符高度',
    noteUniformScaleHelp: '音符宽度（以及按距离的宽度）也会缩放音符高度。',
    attachUiFix: 'UI 绑定修正',
    attachUiFixHelp: 'Phira 对 UI 绑定线使用的 HUD 元素支点（Phira 导入的谱面中默认开启）。',
    lineLength: '判定线长度',
    lineLengthHelp: '普通判定线的半长，以谱面单位计（Phira 默认为 6）。',
    speedClassic: '经典',
    speedRpe170: 'RPE 1.7 流速曲线',
    speedClassicDesc: 'Phira 的默认方式：流速在关键帧之间线性变化；其他缓动在导出时烘焙。',
    speedRpe170Desc: '流速可以跟随任意缓动；其他玩家的速度设置不再有影响，但 RPE 1.7 之前的版本会显示不同的速度。',
    speedSubModern: 'RPEVersion {version}：速度跟随绘制的曲线。',
    speedSubLegacy: 'RPEVersion {version}（低于 170）：RPE 1.7 之前的积分方式。',
    switchToRpe170: '切换到 RPE 1.7 流速曲线…',
    switchToClassic: '切换到经典流速…',
    switchTitle: '切换流速模式',
    switchToRpe170Body: '流速将跟随你绘制的曲线。其他玩家的速度设置不再有影响，但 RPE 1.7 之前的版本会显示不同的速度。',
    switchToClassicBody: '带缓动的流速区段保持不变，并在导出时烘焙为线性分段。',
    rpeVersionQuestion: '此谱面的 RPEVersion 为 {version}。低于 170 时 Phira 处理流速曲线的方式不同：',
    rpeVersionRaise: '将 RPEVersion 提高到 170',
    rpeVersionRaiseDesc: '速度跟随绘制的曲线。',
    rpeVersionKeep: '保持 {version}',
    rpeVersionKeepDesc: 'RPE 1.7 之前的积分方式：速度 = k * 曲线斜率 + b。',
    ignoredEaseQuestion: '有 {count, plural, other {# 个流速区段}}保留了 Phira 目前会忽略的 RPE 缓动：',
    ignoredEaseDrop: '丢弃它们',
    ignoredEaseDropDesc: '谱面的播放效果与现在完全相同。',
    ignoredEaseApply: '应用它们',
    ignoredEaseApplyDesc: '这些缓动会生效。',
    switchConfirm: '切换',
    rpeVersion: 'RPEVersion',
    rpeVersionHelp: '写入谱面的 RPE 版本（永远不会自动提高）。',
    speedBakeTol: '流速烘焙容差',
    speedBakeTolHelp: '烘焙带缓动的流速时允许的最大距离误差，以 RPE 像素为单位。',
    previewStart: '开始',
    previewEnd: '结束',
    previewEndDefault: '开始后 15 秒',
    previewEndCustom: '自定义结束',
    previewHelp: '在曲目列表中播放的曲目片段。',
    previewPlay: '播放预览',
    previewStop: '停止',
    previewSongLength: '曲目时长：{sec} 秒。',
    previewSongUnknown: '曲目时长未知（还没有音乐）。',
    previewIssueStart: '开始必须为 0 或更晚。',
    previewIssueOrder: '结束必须在开始之后。',
    previewIssueBeyond: '在曲目结束之后才结束（上限 {limit} 秒）：Phira 会拒绝打开谱面。',
    previewClamp: '限制在曲目范围内',
    previewOk: '在曲目范围内。',
    exportProfile: '导出配置',
    profileRpe: 'RPE 兼容',
    profileRpeDesc: '添加 RPE 需要的填充（4 个层级位、默认字段、info.txt）。',
    profileMinimal: '精简',
    profileMinimalDesc: '仅供 Phira 使用的最小文件。',
    fileNaming: '文件名',
    namingReadable: '易读（chart.json、music.ogg）',
    namingRpeId: 'RPE ID（{id}.json）',
    infoOffsetHelp: '写入 info.yml 的不足一毫秒的余数：{sec} 秒。',
    psNoProject: '打开一个项目以更改其设置。',
    absentFullLength: '完整长度',
    graphToFar: '{value} px，之后直至无穷远',
    moreLineSettings: '更多判定线设置',
    notSetPlaceholder: '未设置（{what}）',
    noteFakeDesc: '不参与判定，不计入数量，没有打击音。',
    pointEasingButton: '到此点的缓动：{curve}。更改…',
    pointFar: '{x} px：尽可能远，对更远的所有音符保持此效果。',
    pointFarShort: '∞',
    propNotSet: '未设置',
    parentMixed: '多个不同的值',
    parentSearch: '按编号或名称查找判定线',
    parentShowing: '{shown, plural, other {显示 # 条判定线}}，共 {total} 条。输入以缩小列表。',
  },
};

export default catalog;
